import { Suspense, lazy } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ordersApi, reportsApi } from '@/api/endpoints'
import { Empty, ErrorNote, Loading, PageHeader, SourceBadge, StatCard, StatusBadge } from '@/components/common/Ui'
import { money, relative } from '@/lib/format'

// The charting library is the portal's largest dependency and this is the
// landing page: loaded on its own so the figures and the order list are on
// screen before it arrives.
const RevenueChart = lazy(() => import('@/components/dashboard/RevenueChart'))

export default function Dashboard() {
  const { data: stats, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard'],
    queryFn: reportsApi.dashboard,
    refetchInterval: 30_000,
  })

  const { data: recent } = useQuery({
    queryKey: ['orders', { page: 1, page_size: 8 }],
    queryFn: () => ordersApi.list({ page: 1, page_size: 8 }),
  })

  const { data: top } = useQuery({
    queryKey: ['top-products'],
    queryFn: () => reportsApi.topProducts({}),
  })

  if (isLoading) return <Loading />
  if (isError) return <ErrorNote message="Could not load the dashboard" onRetry={refetch} />

  return (
    <div className="dashboard">
      <PageHeader title="Dashboard" subtitle="Today at a glance" />

      <div className="stat-grid">
        <StatCard label="Orders today" value={stats.orders_today} />
        <StatCard label="Revenue today" value={money(stats.revenue_today)} tone="success" />
        <StatCard label="Active customers" value={stats.active_customers} />
      </div>

      <Suspense fallback={<div className="page-loading">Loading chart…</div>}>
        <RevenueChart />
      </Suspense>

      <section className="panel">
        <div className="panel-title">Recent orders</div>
        {!recent?.items?.length ? (
          <Empty title="No orders yet" hint="Orders appear here as customers message in." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Source</th>
                <th>Status</th>
                <th className="right">Total</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {recent.items.map((order) => (
                <tr key={order.id}>
                  <td>
                    <Link to={`/orders/${order.id}`}>{order.order_number}</Link>
                  </td>
                  <td>{order.customer_name}</td>
                  <td>
                    <SourceBadge source={order.source} />
                  </td>
                  <td>
                    <StatusBadge status={order.status} />
                  </td>
                  <td className="right">{money(order.grand_total)}</td>
                  <td className="muted">{relative(order.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="panel">
        <div className="panel-title">Top products · last 30 days</div>
        {!top?.length ? (
          <Empty icon="📦" title="No sales yet" />
        ) : (
          <table className="table compact">
            <thead>
              <tr>
                <th>Item</th>
                <th className="right">Qty (kg)</th>
                <th className="right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {top.map((row) => (
                <tr key={row.item_code}>
                  <td>
                    {row.item_name}
                    <div className="mono muted">{row.item_code}</div>
                  </td>
                  <td className="right">{row.quantity_kg.toLocaleString('en-IN')}</td>
                  <td className="right">{money(row.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}
